import {
  ReportTemplate,
  ReportTemplateContext,
  ReportTemplateResolver,
  Variants,
  mergeAll,
} from '../types/variant';

export * from './status';

import { medGVariants } from './med-g';
import { medGCourtVariants } from './med-g-court';
import { allergoVariants } from './allergo';
import { cardioVariants } from './cardio';
import { etpSommeilVariants } from './etp-sommeil';
import { mpbVariants } from './mpb';
import { sommeilVariants } from './sommeil';
import { genericSpeVariants } from './generic_specialist';
import { radioInterventionnelleVariants } from './radiologie-interventionnelle';
import { anesthesieVariants } from './anesthesie';
import { angioVariants } from './angio';
import { neurologieVariants } from './neurologie';
import { evalComplexeVariants } from './eval-complexe';
import { endocrinologieWellplaceVariants } from './endocrinologie-wellplace';
import { hepatoGastroVariants } from './hepato-gastro-enterologie';
import { promArsVariants } from './prom-ars';
import { kinesitherapieVariants } from './kinesitherapie';
export const config = {
  specialities: [
    'MED-G',
    'MED-G-COURT',
    'ALLERGO',
    'CARDIO',
    'CARDIO-B',
    'ANGIO',
    'ETP-SOMMEIL',
    'MBP',
    'SOMMEIL',
    'GENERIC-SPE',
    'RADIO-INTERVENTIONNELLE',
    'ANESTHESIE',
    'ANESTHESIE-PREOP',
    'SOMMEIL-GENERALISTE',
    'NEUROLOGIE',
    'EVAL-COMPLEXE',
    'ENDOCRINOLOGIE-WELLPLACE',
    'HEPATO-GASTRO-ENTEROLOGIE',
    'PROM-ARS',
    'KINE',
  ],
  patientOnboardings: [
    'DEFAULT',
    'MED_G',
    'SNP_DEMO',
    'SOMMEIL_BLIGNY',
    'SOMMEIL',
    'MON_BILAN_PREVENTION',
    'ETP_SOMMEIL',
    'BORNE',
    'PROM_ARS',
  ],
  patientOffboardings: ['DEFAULT', 'DIRECT_CONFIRMATION', 'CONFIRMATION_ONLY'],
  emailTemplates: ['MED_G_DEFAULT', 'sommeil-bligny', 'mon-bilan-prevention'],
  reportTemplates: [
    'MED_G_DEFAULT',
    'SIMPLE',
    'BASIC_SPECIALTY',
    'ALLERGO',
    'QUESTIONNAIRE',
    'SLEEP',
    'MON_BILAN_PREVENTION',
    'QUESTIONS_ANSWERS',
    'RAW',
    'ANESTHESIA',
    'ENDOCRINOLOGY',
    'FICHE_COORDINATION',
    'CARDIOLOGY',
    'ANGIOLOGY',
    'INTERVENTIONAL_RADIOLOGY',
    // Plain-text "letter" rendering of the RI report (ALDO-2650). The
    // radiologie_interventionnelle resolver maps reports past KB 58.3 to this
    // template; older reports keep INTERVENTIONAL_RADIOLOGY (still registered).
    'INTERVENTIONAL_RADIOLOGY_PLAIN',
    // Même rendu « lettre » pour la kinésithérapie, avec son propre catalogue
    // de sections (bilan spécifique, retentissement fonctionnel, objectifs).
    'KINESITHERAPY_PLAIN',
    'GASTRO',
    'MMG_BORNE',
  ],
  options: [
    'ADDRESSAGE_MMG',
    'ALERTES',
    'ANESTHESIE_CEDRE',
    'ANESTHESIE_ST_ROCH',
    'ANESTHESIE_VICTOIRE',
    'COMPACT',
    'JOURNEE_PREVENTION_MSP_ESPOIRS',
    'LIMITED_MOTIVES',
    'LIMITED_PREVENTION',
    'MMG_BORNE',
    'MON_BILAN_PREVENTION',
    'MSP_HERSANGE_CPTS_CONTACT_CONSENT',
    'SINGLE_MOTIVE',
  ],
} as const;

export const variants: Variants = mergeAll(
  medGVariants,
  medGCourtVariants,
  allergoVariants,
  cardioVariants,
  angioVariants,
  etpSommeilVariants,
  mpbVariants,
  sommeilVariants,
  radioInterventionnelleVariants,
  anesthesieVariants,
  genericSpeVariants,
  neurologieVariants,
  evalComplexeVariants,
  endocrinologieWellplaceVariants,
  hepatoGastroVariants,
  promArsVariants,
  kinesitherapieVariants,
);

/** All valid personalization flag names, derived from all variants' personalizableOptions. */
export const QUESTIONNAIRE_PERSONALIZATION_FLAG_NAMES = [
  ...new Set(
    Object.keys(variants).flatMap((slug) =>
      Object.keys(variants[slug]?.personalizableOptions ?? {}),
    ),
  ),
].sort() as readonly string[];

export type QuestionnairePersonalizationFlag =
  (typeof QUESTIONNAIRE_PERSONALIZATION_FLAG_NAMES)[number];
export type QuestionnaireVariantFlagMap = Partial<
  Record<QuestionnairePersonalizationFlag, boolean>
>;
export type QuestionnaireOptions = Record<string, QuestionnaireVariantFlagMap>;

export function getDefaultConfig(
  variantSlug: string,
): QuestionnaireVariantFlagMap {
  const opts = variants[variantSlug]?.personalizableOptions;
  if (!opts) return {};
  const result: QuestionnaireVariantFlagMap = {};
  for (const key of Object.keys(opts)) {
    result[key as QuestionnairePersonalizationFlag] = opts[key].default;
  }
  return result;
}

/**
 * Resolve a variant's `reportTemplate` field to a concrete template. A variant
 * may declare either a static template or a resolver function that derives it
 * from runtime context (e.g. KB version). All consumers should resolve through
 * this helper rather than reading `reportTemplate` directly. Returns
 * `undefined` when the field is absent (e.g. unknown variant) so callers can
 * apply their own fallback.
 */
export const resolveReportTemplate = (
  template: ReportTemplate | ReportTemplateResolver | undefined,
  context: ReportTemplateContext,
): ReportTemplate | undefined =>
  typeof template === 'function' ? template(context) : template;

export const DEFAULT_DEMO_VARIANT_SLUG = 'snp';

export interface DemoTestableVariant {
  slug: string;
  label: string;
}

export const getDemoTestableVariants = (): DemoTestableVariant[] =>
  Object.entries(variants)
    .filter(([, variant]) => variant?.availableInDemo === true)
    .map(([slug, variant]) => ({
      slug,
      label: variant?.name ?? slug,
    }))
    .sort((a, b) => {
      if (a.slug === DEFAULT_DEMO_VARIANT_SLUG) return -1;
      if (b.slug === DEFAULT_DEMO_VARIANT_SLUG) return 1;
      return a.label.localeCompare(b.label, 'fr');
    });
